import { BrowserAuthErrorMessage } from '@azure/msal-browser'

// MSAL records "an interactive request is in flight" under
// `msal.interaction.status` — in sessionStorage and, because
// storeAuthStateInCookie is on, in a cookie. Cookies ignore the port, so the
// cookie is shared by every tab on the HOST: FMS and the F5 console both run
// on localhost and see each other's marker there (their storages stay
// separate — different ports are different origins). The value is the
// clientId that started the redirect.
//
// Two MSAL rules turn a foreign marker into a dead Sign in button:
//   - loginRedirect refuses to start while a marker of ANY clientId exists;
//   - MSAL only ever removes a marker that carries ITS OWN clientId.
// So a marker left by the other registration is never cleaned, and every
// click throws BrowserAuthError interaction_in_progress. A marker of our own
// clientId is not our problem: MSAL's handleRedirectPromise clears it on the
// next page load, and one that appears after load means another tab of this
// app is signing in right now.
//
// These helpers touch the marker only. The other registration's
// `msal.<clientId>.request.*` cookies are left alone: they are harmless to
// us, and MSAL's own cleanup drops them once that app signs in again. Do not
// confuse this with `clearAllCache` (cacheUtils), which wipes every cookie
// and storage on logout — this is the surgical case for a still-signed-out
// browser.
//
// Key name, cookie attributes and lookup order are transcribed from
// BrowserCacheManager in @azure/msal-browser 2.39.0 (setItemCookie,
// getTemporaryCache) and assume this app's cache config: cacheLocation
// localStorage, storeAuthStateInCookie true, secureCookies off. The
// real-instance test in msalInteraction.test.js is what catches a drift.

export const INTERACTION_STATUS_KEY = 'msal.interaction.status'
export const INTERACTION_IN_PROGRESS = BrowserAuthErrorMessage.interactionInProgress.code

const readCookie = name => {
  const prefix = `${encodeURIComponent(name)}=`
  const hit = document.cookie
    .split(';')
    .map(cookie => cookie.trim())
    .find(cookie => cookie.indexOf(prefix) === 0)
  return hit ? decodeURIComponent(hit.substring(prefix.length)) : ''
}

const readStorage = storage => {
  try {
    return storage.getItem(INTERACTION_STATUS_KEY) || ''
  } catch (error) {
    return ''
  }
}

const removeFromStorage = storage => {
  try {
    storage.removeItem(INTERACTION_STATUS_KEY)
  } catch (error) {
    // Storage may be blocked (private mode, disabled cookies); nothing to clear then.
  }
}

// Same lookup order MSAL uses: cookie first, then session, then the legacy
// localStorage slot older versions wrote to.
export const readInteractionMarker = () => {
  return readCookie(INTERACTION_STATUS_KEY) || readStorage(window.sessionStorage) || readStorage(window.localStorage)
}

// Same attributes MSAL writes the cookie with, so the browser matches and
// expires the right one.
export const clearInteractionMarker = () => {
  removeFromStorage(window.sessionStorage)
  removeFromStorage(window.localStorage)
  document.cookie = `${encodeURIComponent(INTERACTION_STATUS_KEY)}=;path=/;SameSite=Lax;expires=Thu, 01 Jan 1970 00:00:00 GMT;`
}

// Drops a marker that belongs to a different clientId. This build's MSAL can
// neither honour nor remove such a marker, so losing it costs us nothing; it
// does cost the OTHER app its in-flight redirect, if one is running in a
// sibling tab, which is why the sweep says so in the console. Returns whether
// anything was cleared. Our own marker is left for MSAL.
export const clearForeignInteractionMarker = clientId => {
  const marker = readInteractionMarker()
  if (!marker || marker === clientId) return false
  clearInteractionMarker()
  console.warn(`Dropped a stale MSAL interaction marker left by another registration (${marker}) on this host.`)
  return true
}

export const isInteractionInProgressError = error => error?.errorCode === INTERACTION_IN_PROGRESS

// Runs an interactive MSAL call. If MSAL refuses because a marker is set and
// that marker is foreign, drop it and try once more; a marker of our own
// clientId means a redirect of this app is genuinely in flight (another tab,
// or a second click here), so the error surfaces untouched, as does any
// other error and any second refusal.
export const withStaleInteractionRecovery = async (run, clientId) => {
  try {
    return await run()
  } catch (error) {
    if (!isInteractionInProgressError(error) || !clearForeignInteractionMarker(clientId)) throw error
    return run()
  }
}
